import { GatewayResult as HttpGatewayResult } from '@utils/http-feature/contracts';
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MagazzinoDispatchOperation, MagazzinoDispatchRequest, MagazzinoResponse } from '@common/dto/magazzino.dto';
import { IWarehouseError, SerialCodeUniqueResponse } from '@common/dto/magazzino.types';
import { API_PLACEHOLDER } from "@ep-om/project/pages/warehouse/warehouse.types";
import { TranslateService } from "@ngx-translate/core";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "../../../../../environments/environment";


const dispatchUrl = `${environment.apiUrl}/${API_PLACEHOLDER}`;

/** Every gateway call resolves to exactly one of these; the stream never errors. */
export type GatewayResult<T> = HttpGatewayResult<T, IWarehouseError>;

export type DispatchOptions<T> = {
  /** Returned as `response` (next to the error) when the call fails. */
  mock?: T;
  /** Skip the error toast: the caller routes the error through its own `error$` stream. */
  silent?: boolean;
};

const FALLBACK_ERROR: IWarehouseError = {
  code: '',
  message: '[Fallback] Something wrong happened.',
  details: {}
};

@Injectable({
  providedIn: 'any'
})
export class WarehouseHttpService {

  constructor(
    private _http: HttpClient,
    private _nzns: NzNotificationService,
    private _translateSvc: TranslateService
  ) { }

  /**
   * Forwards one operation to Gisfo through the backend proxy. `params`, `body` and the response type
   * are derived from `dto.operation` (`MagazzinoDtoList` / `MagazzinoResponseList` in `@common`):
   * callers never declare them.
   */
  dispatch<Op extends MagazzinoDispatchOperation>(
    dto: MagazzinoDispatchRequest<Op>,
    options: DispatchOptions<MagazzinoResponse<Op>> = {}
  ): Observable<GatewayResult<MagazzinoResponse<Op>>> {
    return this._http.post<MagazzinoResponse<Op>>(dispatchUrl, dto).pipe(
      map(response => ({response})),
      catchError((errResp: HttpErrorResponse) => {
        const error = errResp?.error ?? FALLBACK_ERROR;
        const content = `[${dto.operation}]:: ${error?.message}`;
        console.error(content);
        if (!options.silent) {
          this._nzns.error(this._translateSvc.instant('ERROR'), content);
        }
        return of(options.mock === undefined ? {error} : {response: options.mock, error});
      })
    );
  }

  isSerialCodeUnique(serialCode: string): Observable<GatewayResult<SerialCodeUniqueResponse>> {
    const url = `${environment.apiUrl}/api/magazzino/serial-code/unique`;

    return this._http.get<SerialCodeUniqueResponse>(url, { params: { serialCode } }).pipe(
      map(response => ({response})),
      catchError((errResp: HttpErrorResponse) => {
        const error = errResp?.error ?? FALLBACK_ERROR;
        console.error(`[serialCodes.unique]:: ${error?.message}`);
        return of({error});
      })
    );
  }
}
